// /corporate-accounts — Corporate Accounts (PAGES.md §3.101)
//
// B2B account management — manage corporate clients for bulk ordering
// with credit terms, GSTIN invoicing, and delivery scheduling.
// SHARED vertical. No feature flag. Min role: MANAGER (30) view, OWNER (50) create.
// Traceability: FR-CORP-001

import { getSubunitMultiplier } from '@bbpos/utils';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Badge,
  Select,
  Input,
  TextArea,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  DataTable,
  Pagination,
  Modal,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { ROLE_LEVELS } from '@/types/user';
import { useAuthStore } from '@/stores/use-auth-store';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { useTenantDial } from '@/hooks/use-tenant-dial';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { corporateAccountService } from '@/services/corporate-account.service';
import type { CorporateAccount, CreateCorporateAccountDto } from '@/services/corporate-account.service';
import { ApiError } from '@/lib/api-client';
import { GLASS, GLASS_FILTER, GLASS_INTERACTIVE, cn } from '@/constants/design-tokens';

// ─── Constants ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const PHONE_REGEX = /^\+[1-9]\d{6,14}$/;

// ─── Icons ─────────────────────────────────────────────────────────────────

function _BuildingIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v.01M12 14v.01M16 14v.01M8 18v.01M12 18v.01M16 18v.01" />
    </svg>
  );
}

function BuildingEmptyIcon() {
  return (
    <svg className="h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v.01M12 14v.01M16 14v.01M8 18v.01M12 18v.01M16 18v.01" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function _CurrencyIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="6" y1="3" x2="6" y2="15" /><path d="M6 3h8a4 4 0 010 8H6" /><path d="M14 11l4 10" /><line x1="4" y1="3" x2="12" y2="3" /><line x1="4" y1="7" x2="16" y2="7" />
    </svg>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function outstandingColor(outstanding: number, creditLimit: number): string {
  if (outstanding === 0) return 'text-success-600 dark:text-success-400';
  // creditLimit >= 0 covers a real limit (incl. 0 = no credit); a negative
  // limit is "unlimited", which can never be over.
  if (creditLimit >= 0 && outstanding > creditLimit) return 'text-danger-600 dark:text-danger-400';
  if (outstanding > 0) return 'text-warning-600 dark:text-warning-400';
  return 'text-neutral-700 dark:text-neutral-300';
}

// ─── Mobile Card ────────────────────────────────────────────────────────────

function AccountMobileCard({ account, onClick }: { account: CorporateAccount; onClick: () => void }) {
  const currency = useTenantCurrency();
  return (
    <div className={cn(GLASS_INTERACTIVE, 'p-4 animate-slide-up')} onClick={onClick}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-neutral-900 dark:text-neutral-100 truncate">{account.company_name}</p>
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mt-0.5">{account.contact_name}</p>
          {account.gstin && (
            <p className="text-xs font-mono tracking-wide text-neutral-500 dark:text-neutral-400 mt-0.5">{account.gstin}</p>
          )}
        </div>
        <Badge variant={account.is_active ? 'success' : 'draft'} size="sm">
          {account.is_active ? 'Active' : 'Inactive'}
        </Badge>
      </div>
      <div className="flex items-center justify-between mt-3">
        <span className={cn('text-sm font-bold font-mono tabular-nums', outstandingColor(account.outstanding_balance, account.credit_limit))}>
          {formatPaise(account.outstanding_balance, currency)} outstanding
        </span>
        <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
          {formatPaise(account.credit_limit, currency)}
        </span>
      </div>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

function CorporateAccountsListContent() {
  const dial = useTenantDial();
  const { toast } = useToast();
  const navigate = useNavigate();
  const currency = useTenantCurrency();
  // Statutory tax-id vocabulary/validation derives from the immutable currency.
  // INR → GST_INDIA: label `GSTIN`, GSTIN regex (byte-identical to before).
  // GCC → `TRN`/`VATIN`/… + 15-digit regex; Qatar/Kuwait have no statutory id
  // (`taxIdLabel === null`) ⇒ the field is hidden entirely.
  const tax = useTaxRegime();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchRef = useRef<HTMLInputElement | null>(null);

  // Role
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const canCreate = roleLevel >= ROLE_LEVELS.OWNER;

  // URL-synced filters
  const urlSearch = searchParams.get('search') ?? '';
  const urlStatus = searchParams.get('status') ?? '';
  const urlPage = Number(searchParams.get('page') ?? '1');

  const [searchQuery, setSearchQuery] = useState(urlSearch);
  // Debounced search term — the input updates `searchQuery` on every keystroke
  // for responsiveness, but only this committed value drives the server request.
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearch);
  const [statusFilter, setStatusFilter] = useState(urlStatus);
  const [page, setPage] = useState(urlPage);

  // Debounce: wait for a ~300ms typing pause before committing the search term.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(id);
  }, [searchQuery]);

  // Reset to page 1 only when the committed search actually changes (not on
  // every keystroke, and not on the initial mount which would clobber ?page=N).
  const isFirstSearchSync = useRef(true);
  useEffect(() => {
    if (isFirstSearchSync.current) {
      isFirstSearchSync.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (debouncedSearch) params.search = debouncedSearch;
    if (statusFilter) params.status = statusFilter;
    if (page > 1) params.page = String(page);
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, statusFilter, page, setSearchParams]);

  // Data
  const [accounts, setAccounts] = useState<CorporateAccount[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Guards against out-of-order responses: a slower earlier request must not
  // overwrite the results of a later one (the in-flight request can't be
  // cancelled, so we ignore its result once superseded).
  const requestSeq = useRef(0);

  const fetchData = useCallback(async () => {
    const seq = ++requestSeq.current;
    setIsLoading(true);
    try {
      const result = await corporateAccountService.list({
        page,
        search: debouncedSearch || undefined,
        status: statusFilter || undefined,
      });
      if (seq !== requestSeq.current) return; // superseded by a newer request
      setAccounts(result.data);
      setTotalItems(result.meta.total);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load accounts.', variant: 'error' });
    } finally {
      if (seq === requestSeq.current) setIsLoading(false);
    }
  }, [page, debouncedSearch, statusFilter, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Create modal
  const [modalOpen, setModalOpen] = useState(false);
  const [formCompanyName, setFormCompanyName] = useState('');
  const [formGstin, setFormGstin] = useState('');
  const [formContactName, setFormContactName] = useState('');
  const [formContactPhone, setFormContactPhone] = useState('');
  const [formContactEmail, setFormContactEmail] = useState('');
  const [formCreditLimitRupees, setFormCreditLimitRupees] = useState('');
  const [formBillingAddress, setFormBillingAddress] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const openCreateModal = useCallback(() => {
    setFormCompanyName('');
    setFormGstin('');
    setFormContactName('');
    setFormContactPhone('');
    setFormContactEmail('');
    setFormCreditLimitRupees('');
    setFormBillingAddress('');
    setFormError(null);
    setModalOpen(true);
  }, []);

  const handleCreate = useCallback(async () => {
    if (!formCompanyName.trim()) { setFormError('Company name is required'); return; }
    if (!formContactName.trim()) { setFormError('Contact person is required'); return; }
    if (!PHONE_REGEX.test(formContactPhone)) { setFormError('Enter a valid phone number (e.g. +919876543210)'); return; }
    if (formGstin && tax.taxIdRegex && !tax.taxIdRegex.test(formGstin.toUpperCase())) { setFormError(`Invalid ${tax.taxIdLabel ?? 'tax ID'} format`); return; }
    if (formContactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formContactEmail)) { setFormError('Invalid email format'); return; }

    // Credit limit is mandatory. 0 = no credit; a positive number = that limit.
    if (formCreditLimitRupees.trim() === '') {
      setFormError('Credit limit is required (enter 0 for no credit)'); return;
    }
    const creditLimitPaise = Math.round(Number(formCreditLimitRupees) * getSubunitMultiplier(currency));
    if (!Number.isFinite(creditLimitPaise) || creditLimitPaise < 0) {
      setFormError('Invalid credit limit'); return;
    }

    setIsSaving(true);
    setFormError(null);
    try {
      const dto: CreateCorporateAccountDto = {
        company_name: formCompanyName.trim(),
        contact_name: formContactName.trim(),
        contact_phone: formContactPhone.trim(),
        gstin: formGstin.toUpperCase().trim() || undefined,
        contact_email: formContactEmail.trim() || undefined,
        credit_limit: creditLimitPaise,
        billing_address: formBillingAddress.trim() || undefined,
      };
      const created = await corporateAccountService.create(dto);
      toast({ title: `Corporate account '${created.company_name}' created`, variant: 'success' });
      setModalOpen(false);
      navigate(`/corporate-accounts/${created.id}`);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Failed to create account');
    } finally {
      setIsSaving(false);
    }
  }, [formCompanyName, formContactName, formContactPhone, formGstin, formContactEmail, formCreditLimitRupees, formBillingAddress, navigate, tax, toast]);

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (e.key === 'n' && !modalOpen && canCreate) { e.preventDefault(); openCreateModal(); }
      if (e.key === '/') { e.preventDefault(); searchRef.current?.focus(); }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen, canCreate, openCreateModal]);

  useEffect(() => { document.title = 'Corporate Accounts — Orio360 Admin'; }, []);

  // Table columns (TYPOGRAPHY §24)
  const columns: ColumnDef<CorporateAccount>[] = useMemo(() => [
    {
      key: 'company_name', header: 'Company',
      render: (_v: unknown, row: CorporateAccount) => (
        <Link
          to={`/corporate-accounts/${row.id}`}
          className="group flex items-center gap-2 -my-1 rounded-lg px-2 py-1 transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
        >
          <span className="text-base font-semibold text-neutral-900 dark:text-neutral-100 group-hover:text-indigo-700 dark:group-hover:text-indigo-300">
            {row.company_name}
          </span>
        </Link>
      ),
    },
    {
      key: 'gstin', header: tax.taxIdLabel ?? 'Tax ID',
      render: (_v: unknown, row: CorporateAccount) => (
        row.gstin
          ? <span className="text-xs font-normal font-mono tracking-wide text-neutral-500 dark:text-neutral-400">{row.gstin}</span>
          : <span className="text-xs text-neutral-400 dark:text-neutral-500">—</span>
      ),
    },
    {
      key: 'contact_name', header: 'Contact',
      render: (_v: unknown, row: CorporateAccount) => (
        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{row.contact_name}</span>
      ),
    },
    {
      key: 'contact_phone', header: 'Phone',
      render: (_v: unknown, row: CorporateAccount) => (
        <span className="text-sm font-mono tabular-nums text-neutral-600 dark:text-neutral-400">{row.contact_phone}</span>
      ),
    },
    {
      key: 'credit_limit', header: 'Credit Limit', numeric: true,
      render: (_v: unknown, row: CorporateAccount) => (
        <span className="text-sm font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">
          {formatPaise(row.credit_limit, currency)}
        </span>
      ),
    },
    {
      key: 'outstanding_balance', header: 'Outstanding', numeric: true,
      render: (_v: unknown, row: CorporateAccount) => (
        <span className={cn('text-sm font-bold font-mono tabular-nums', outstandingColor(row.outstanding_balance, row.credit_limit))}>
          {formatPaise(row.outstanding_balance, currency)}
        </span>
      ),
    },
    {
      key: 'is_active', header: 'Status',
      render: (_v: unknown, row: CorporateAccount) => (
        <Badge variant={row.is_active ? 'success' : 'draft'} size="sm">
          {row.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ], [currency, tax]);

  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  return (
    <PageContainer
      title="Corporate Accounts"
      subtitle="Manage corporate clients for bulk ordering and invoicing"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Corporate Accounts' },
      ]}
      actions={
        canCreate ? (
          <Button variant="primary" size="sm" onClick={openCreateModal}>
            <PlusIcon /><span className="ml-1.5">Add Account</span>
          </Button>
        ) : undefined
      }
    >
      <div className="animate-fade-in">
        {/* Header Gradient (indigo→violet for B2B/CRM domain) */}
        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(99,102,241,0.45)] mb-6 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
          <div className="relative">
            <h1 className="text-2xl font-bold text-white">Corporate Accounts</h1>
            <p className="text-indigo-100 mt-1">Manage B2B clients with credit terms, bulk orders, and {tax.taxName} invoicing</p>
          </div>
        </div>

        {/* Filters */}
        <div className={cn(GLASS_FILTER, 'p-4 mb-6')}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <Input
                ref={searchRef}
                placeholder="Search by company name..."
                value={searchQuery}
                onChange={(v) => setSearchQuery(typeof v === 'string' ? v : '')}
              />
            </div>
            <div className="w-44">
              <Select
                options={STATUS_FILTER_OPTIONS}
                value={statusFilter}
                onChange={(val) => {
                  setStatusFilter(typeof val === 'string' ? val : val[0] ?? '');
                  setPage(1);
                }}
                placeholder="All Statuses"
              />
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className={cn(GLASS, 'p-6 space-y-4')}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex gap-4">
                <Skeleton variant="text" width="20%" height={18} />
                <Skeleton variant="text" width="15%" height={18} />
                <Skeleton variant="text" width="15%" height={18} />
                <Skeleton variant="text" width="12%" height={18} />
                <Skeleton variant="text" width="12%" height={18} />
                <Skeleton variant="text" width="12%" height={18} />
                <Skeleton variant="rect" width={60} height={24} />
              </div>
            ))}
          </div>
        ) : accounts.length === 0 ? (
          <EmptyState
            variant="page"
            icon={<BuildingEmptyIcon />}
            title="No corporate accounts"
            description="Add corporate clients for bulk ordering and invoicing"
            action={canCreate ? { label: 'Add Corporate Account', onClick: openCreateModal } : undefined}
          />
        ) : (
          <>
            {/* Desktop DataTable */}
            <div className={cn(GLASS, 'overflow-hidden hidden sm:block mb-6')}>
              <div className="px-5 py-4">
                <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                  Accounts ({totalItems.toLocaleString('en-IN')})
                </h3>
              </div>
              <DataTable<CorporateAccount>
                columns={columns}
                data={accounts}
              />
            </div>

            {/* Mobile Cards */}
            <div className="block sm:hidden space-y-3 mb-6">
              <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider px-1">
                Accounts ({totalItems.toLocaleString('en-IN')})
              </h3>
              {accounts.map((account) => (
                <AccountMobileCard
                  key={account.id}
                  account={account}
                  onClick={() => navigate(`/corporate-accounts/${account.id}`)}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mb-6">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={PAGE_SIZE}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Mobile FAB */}
      {canCreate && (
        <button
          type="button"
          onClick={openCreateModal}
          className="fixed bottom-6 right-6 sm:hidden z-40 w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center active:scale-95"
          aria-label="Add Corporate Account"
        >
          <PlusIcon />
        </button>
      )}

      {/* Create Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Add Corporate Account" variant="wide">
        <div className="space-y-4 p-1 max-h-[70vh] overflow-y-auto">
          {formError && (
            <div className="text-xs text-danger-600 bg-danger-50 dark:bg-danger-900/20 px-3 py-2 rounded-lg">
              {formError}
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Company Name *</label>
            <Input
              value={formCompanyName}
              onChange={(e) => { setFormCompanyName(typeof e === 'string' ? e : ''); setFormError(null); }}
              placeholder="e.g. Tata Consultancy Services"
            />
          </div>

          {/* Statutory tax id — `GSTIN` (India) / `TRN`·`VATIN`·… (GCC).
              Qatar/Kuwait have no statutory id ⇒ field hidden. */}
          {tax.taxIdLabel && (
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Company {tax.taxIdLabel}</label>
              <Input
                value={formGstin}
                onChange={(e) => setFormGstin(typeof e === 'string' ? e.toUpperCase() : '')}
                placeholder={tax.splitLabels ? 'e.g. 27AAPCT1234F1Z5' : 'e.g. 100123456700003'}
                className="font-mono tracking-wide"
              />
              {tax.taxIdHint && (
                <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-1">{tax.taxIdHint}</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Contact Person *</label>
              <Input
                value={formContactName}
                onChange={(e) => { setFormContactName(typeof e === 'string' ? e : ''); setFormError(null); }}
                placeholder="e.g. Rakesh Kumar"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Contact Phone *</label>
              <Input
                variant="phone"
                defaultDial={dial}
                value={formContactPhone}
                onChange={(e) => { setFormContactPhone(typeof e === 'string' ? e : ''); setFormError(null); }}
                placeholder="98765 43210"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Contact Email</label>
              <Input
                value={formContactEmail}
                onChange={(e) => setFormContactEmail(typeof e === 'string' ? e : '')}
                placeholder="accounts@company.com"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Credit Limit ({getCurrencySymbol(currency)}) *</label>
              <Input
                type="number"
                value={formCreditLimitRupees}
                onChange={(e) => { setFormCreditLimitRupees(typeof e === 'string' ? e : ''); setFormError(null); }}
                placeholder="e.g. 50000 · 0 = No credit"
              />
              <p className="text-[10px] text-neutral-400 dark:text-neutral-500 mt-1">Required. Enter 0 for no credit.</p>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Billing Address</label>
            <TextArea
              value={formBillingAddress}
              onChange={(val) => setFormBillingAddress(val)}
              placeholder="Company billing address..."
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" size="sm" onClick={() => setModalOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleCreate} disabled={isSaving}>
              {isSaving ? 'Creating...' : 'Create Account'}
            </Button>
          </div>
        </div>
      </Modal>
    </PageContainer>
  );
}

export function CorporateAccountsListPage() {
  return (
    <ErrorBoundary>
      <CorporateAccountsListContent />
    </ErrorBoundary>
  );
}
